import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { ArrowLeft, ExternalLink, Clock, Newspaper } from "lucide-react";

function timeAgo(iso) {
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
function sourceHost(url) {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}
function fmtPublished(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `Published ${date}, ${time} IST`;
  } catch { return ""; }
}
function firstImageFromHtml(html) {
  if (!html) return null;
  const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (!m) return null;
  const src = m[1];
  if (src.startsWith("data:")) return null;
  return src;
}

export default function NewsDetail() {
  const { slug } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["news-detail", slug],
    queryFn: async () => (await api.get(`/news/${slug}`)).data,
    staleTime: 60_000,
  });

  if (isLoading) return <div className="p-10 text-[#8C8D90] font-mono-fin">Loading article…</div>;
  if (error || !data?.data) {
    return (
      <div className="px-4 sm:px-6 lg:px-12 py-10 max-w-3xl mx-auto">
        <Link to="/news" className="inline-flex items-center gap-2 text-xs font-mono-fin uppercase tracking-widest text-[#6B6C6F] hover:text-[#D91E2A] mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Newsroom
        </Link>
        <div className="ns-card p-8 text-center">
          <Newspaper className="w-8 h-8 text-[#8C8D90] mx-auto mb-3" />
          <h1 className="font-heading font-bold text-xl text-[#1A1A1A] mb-2">Article no longer available</h1>
          <p className="text-sm text-[#6B6C6F]">This story rolled off the live feed. Head back to see the latest.</p>
        </div>
      </div>
    );
  }

  const n = data.data;
  const related = data.related || [];
  const host = sourceHost(n.link);
  const clean = (n.full_html && n.full_html.length > 400 ? n.full_html : (n.content_html || n.summary || "")).trim();
  const hasFullArticle = !!(n.full_html && n.full_html.length > 400);
  const author = n.author || n.source || "Nivesh Street Desk";
  const DEFAULT_OG = "https://niveshstreet.in/og-social-card.jpg";
  // Prefer the article's own image for social shares; absolutize relative paths; fall back to the branded card.
  const rawOg = n.image || firstImageFromHtml(clean) || DEFAULT_OG;
  const ogImage = rawOg.startsWith("http") ? rawOg : `https://niveshstreet.in${rawOg}`;

  return (
    <article className="px-4 sm:px-6 lg:px-10 py-8 max-w-[760px] mx-auto">
      <Helmet>
        <title>{`${n.title} · Nivesh Street`}</title>
        <meta name="description" content={(n.summary || "").replace(/<[^>]*>/g, "").slice(0, 160)} />
        <meta property="og:site_name" content="Nivesh Street" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={n.title} />
        <meta property="og:description" content={(n.summary || "").replace(/<[^>]*>/g, "").slice(0, 200)} />
        <meta property="og:url" content={`https://niveshstreet.in/news/${slug}`} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:section" content={n.category || "Markets"} />
        <meta property="article:published_time" content={n.published} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={n.title} />
        <meta name="twitter:description" content={(n.summary || "").replace(/<[^>]*>/g, "").slice(0, 200)} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={`https://niveshstreet.in/news/${slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: n.title,
          image: ogImage ? [ogImage] : undefined,
          datePublished: n.published,
          dateModified: n.published,
          author: [{ "@type": "Organization", name: n.source || "Nivesh Street" }],
          publisher: { "@type": "Organization", name: "Nivesh Street", logo: { "@type": "ImageObject", url: DEFAULT_OG } },
          articleSection: n.category,
          mainEntityOfPage: `https://niveshstreet.in/news/${slug}`,
          isBasedOn: n.link || undefined,
        })}</script>
      </Helmet>

      <div className="flex justify-center mb-5">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-[11px] font-mono-fin uppercase tracking-[0.25em] text-[#6B6C6F] hover:text-[#D91E2A]"
          data-testid="news-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Newsroom
        </Link>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="ns-eyebrow" style={{ fontSize: 11 }}>{(n.category || "MARKETS").toUpperCase()}</span>
        <span className="text-[11px] font-mono-fin text-[#8C8D90] inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {timeAgo(n.published)} ago
        </span>
      </div>

      <h1
        className="font-heading font-extrabold tracking-tight text-[#1A1A1A] mb-6 leading-[1.1] text-[clamp(2rem,5vw,3.25rem)]"
        data-testid="news-title"
      >
        {n.title}
      </h1>

      <div className="text-sm text-[#1A1A1A] font-semibold mb-1">{author}</div>
      <div className="text-xs text-[#6B6C6F] mb-6">{fmtPublished(n.published)}</div>

      <div className="h-px bg-[#E2DDD3] mb-6" />

      <div
        className="ns-prose"
        data-testid="news-body"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: clean }}
      />

      <div className="mt-10 pt-5 border-t border-[#E2DDD3] flex items-center justify-between flex-wrap gap-3">
        <span className="text-xs text-[#6B6C6F]">
          {hasFullArticle ? (
            <>Originally reported by <span className="text-[#1A1A1A] font-semibold">{n.source}</span>.</>
          ) : (
            <>Curated summary — originally published by <span className="text-[#1A1A1A] font-semibold">{n.source}</span>.</>
          )}
        </span>
        {n.link && (
          <a
            href={n.link}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D91E2A] hover:text-[#B8141F]"
            data-testid="news-source-link"
          >
            Source: {host} <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {related.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center mb-4">
            <span className="ns-rule" />
            <h2 className="font-heading text-xl text-[#1A1A1A]">Related reads</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {related.map(r => (
              <Link
                key={r.slug}
                to={`/news/${r.slug}`}
                className="block ns-card p-4 hover:border-[#D91E2A]/40 transition-colors group"
                data-testid={`related-news-${r.slug}`}
              >
                <div className="ns-eyebrow mb-1.5">{r.source} · {timeAgo(r.published)}</div>
                <div className="text-sm font-semibold text-[#1A1A1A] group-hover:text-[#D91E2A] leading-snug">{r.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}
